// export default Checkout;
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "./Checkout.css";

declare global {
  interface Window { Razorpay: any; }
}

const Checkout: React.FC = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { blog, test } = (location.state as any) || {};

  const [coupon,      setCoupon]      = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [message,     setMessage]     = useState("");
  const [msgOk,       setMsgOk]       = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [paymentError, setPaymentError] = useState("");

  // Base price never mutates — the coupon discount is always derived from
  // it, rather than repeatedly subtracting from a running "finalAmount"
  // state (which is how the old client-only NEW50 check worked, and is
  // fragile the moment you want to remove/re-apply a coupon).
  const baseAmount = blog ? Number(blog.discounted_price) : 0;
  const finalAmount = Math.max(0, baseAmount - couponDiscount);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !blog) navigate("/");
  }, [blog, navigate]);

  const applyCoupon = async () => {
    if (couponApplied || !coupon.trim()) return;
    try {
      setCouponLoading(true);
      setMessage("");
      const token = localStorage.getItem("token");
      const res = await fetch("https://api.acadebility.com/api/v1/coupon/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          code: coupon.trim(),
          amount: baseAmount,
          testBlogId: blog._id,
        }),
      });
      const result = await res.json().catch(() => null);

      if (!res.ok || !result?.valid) {
        setMsgOk(false);
        setMessage(result?.message || "Invalid coupon code. Please try again.");
        return;
      }

      setCouponDiscount(result.discountAmount);
      setCouponApplied(true);
      setMsgOk(true);
      setMessage(result.message || `Coupon applied! You saved ₹${result.discountAmount}`);
    } catch {
      setMsgOk(false);
      setMessage("Couldn't validate that coupon right now. Please try again.");
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCoupon("");
    setCouponApplied(false);
    setCouponDiscount(0);
    setMessage("");
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      setPaymentError("");
      const token = localStorage.getItem("token");
      const res = await fetch("https://api.acadebility.com/api/v1/payment/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          // IMPORTANT: send baseAmount, NOT finalAmount. finalAmount already
          // has the coupon discount subtracted client-side (for display in
          // the Order Summary). If we sent finalAmount here, the backend
          // would apply the coupon discount a SECOND time on top of an
          // already-discounted number — e.g. ₹299 → ₹249 client-side, then
          // the backend does 249 − 50 = ₹199. Sending baseAmount means the
          // backend (the only source of truth for the actual charge) does
          // the ₹299 − ₹50 = ₹249 math exactly once.
          amount: baseAmount,
          testBlogSlug: blog.slug,
          testBlogId: blog._id,
          testId: test?._id,
          // Enroll Now now sends a pseudo-test whose _id equals blog._id
          // (see TestSeriesNormal.tsx) so testId is never empty. Detect that
          // case here rather than relying on `test` being null/not-null.
          purchaseType: test?._id && test._id !== blog._id ? "single_test" : "full_package",
          couponCode: couponApplied ? coupon.trim().toUpperCase() : undefined,
        }),
      });

      const order = await res.json().catch(() => null);

      // Previously any bad/failed response here still fell through to
      // `new window.Razorpay(options).open()` with undefined fields, which threw
      // and landed in the catch below with zero information about the real cause.
      if (!res.ok || !order?.key || !order?.orderId) {
        console.error("create-order failed:", order);
        setPaymentError(
          order?.message || order?.error || "Could not start payment. Please try again."
        );
        setLoading(false);
        return;
      }

      const options = {
        key: order.key,
        amount: order.amount,
        currency: "INR",
        name: "Acadebility",
        description: blog.header_title,
        order_id: order.orderId,
        handler: async (response: any) => {
          try {
            const verifyRes = await fetch("https://api.acadebility.com/api/v1/payment/verify", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify(response),
            });
            const data = await verifyRes.json();
            navigate(data.success ? "/checkout/success" : "/checkout/failure");
          } catch {
            navigate("/checkout/failure");
          }
        },
        modal: {
          // Without this, closing the Razorpay popup without paying left the
          // button stuck on "Processing payment…" forever.
          ondismiss: () => setLoading(false),
        },
        theme: { color: "#0d9e8a" },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      console.error("Payment error:", err);
      setPaymentError("Something went wrong while starting the payment. Please try again.");
      setLoading(false);
    }
  };

  if (!blog) return (
    <div className="ckout-page">
      <div className="ckout-invalid">
        <span className="ckout-invalid-icon">🛒</span>
        <h2>Invalid Checkout</h2>
        <p>No product found. Please go back and try again.</p>
        <Link to="/" className="ckout-back-btn">Go to Home</Link>
      </div>
    </div>
  );

  const originalPrice  = Number(blog.original_price);
  const discountedBase = Number(blog.discounted_price);
  const baseDiscount   = originalPrice - discountedBase;
  const totalSaved     = baseDiscount + couponDiscount;

  const INCLUDED = [
    "All full-length mock tests",
    "Unlimited reattempts",
    "Instant results & solutions",
    "Detailed performance analytics",
    "All-India rank comparison",
    "Lifetime access to content",
  ];

  return (
    <div className="ckout-page">
      <div className="ckout-inner">

        {/* ── Breadcrumb ── */}
        <div className="ckout-breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/online-test-series">Test Series</Link>
          <span>/</span>
          <span>Checkout</span>
        </div>

        <h1 className="ckout-page-title">Complete your purchase</h1>

        <div className="ckout-grid">

          {/* ── LEFT: What's included ── */}
          <div className="ckout-left">

            {/* Product card */}
            <div className="ckout-product-card">
              <div className="ckout-product-top">
                <div className="ckout-product-icon">📚</div>
                <div>
                  <div className="ckout-product-name">{blog.header_title}</div>
                  <div className="ckout-product-sub">{blog.header_sub_title || "Complete Test Series Package"}</div>
                </div>
              </div>
              <div className="ckout-product-pills">
                <span className="ckout-pill"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>{blog.test_count || "45+"} Tests</span>
                <span className="ckout-pill"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>3 hrs / test</span>
                <span className="ckout-pill ckout-pill-green">Lifetime Access</span>
              </div>
            </div>

            {/* What's included */}
            <div className="ckout-includes">
              <h3 className="ckout-includes-title">What's included</h3>
              <ul className="ckout-includes-list">
                {INCLUDED.map(item => (
                  <li key={item}>
                    <span className="ckout-check">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Trust badges */}
            <div className="ckout-trust">
              <div className="ckout-trust-item"><span>🔒</span> 100% Secure Payment</div>
              <div className="ckout-trust-item"><span>↩️</span> 7-day Refund Policy</div>
              <div className="ckout-trust-item"><span>🏅</span> 1L+ Students Trust Us</div>
            </div>
          </div>

          {/* ── RIGHT: Order summary + payment ── */}
          <div className="ckout-right">
            <div className="ckout-summary-card">
              <h3 className="ckout-summary-title">Order Summary</h3>

              {/* Price breakdown */}
              <div className="ckout-price-breakdown">
                <div className="ckout-price-row">
                  <span>Original price</span>
                  <span className="ckout-strikethrough">₹{originalPrice.toLocaleString()}</span>
                </div>
                <div className="ckout-price-row ckout-price-row-green">
                  <span>Discount ({Math.round(baseDiscount / originalPrice * 100)}% OFF)</span>
                  <span>− ₹{baseDiscount.toLocaleString()}</span>
                </div>
                {couponApplied && (
                  <div className="ckout-price-row ckout-price-row-green">
                    <span>Coupon ({coupon.trim().toUpperCase()})</span>
                    <span>− ₹{couponDiscount.toFixed(0)}</span>
                  </div>
                )}
                <div className="ckout-price-divider" />
                <div className="ckout-price-row ckout-price-row-total">
                  <span>Total</span>
                  <span>₹{finalAmount.toLocaleString()}</span>
                </div>
                {totalSaved > 0 && (
                  <div className="ckout-savings-badge">
                    🎉 You're saving ₹{totalSaved.toLocaleString()} on this purchase!
                  </div>
                )}
              </div>

              {/* Coupon */}
              <div className="ckout-coupon-wrap">
                <label className="ckout-coupon-label">Have a coupon or referral code?</label>
                <div className="ckout-coupon-row">
                  <input
                    type="text"
                    className={`ckout-coupon-input${couponApplied ? " ckout-coupon-applied" : ""}`}
                    placeholder="Enter code e.g. ACAD50"
                    value={coupon}
                    disabled={couponApplied || couponLoading}
                    onChange={e => { setCoupon(e.target.value); setMessage(""); }}
                  />
                  {couponApplied ? (
                    <button className="ckout-coupon-btn ckout-coupon-remove-btn" onClick={removeCoupon}>
                      Remove
                    </button>
                  ) : (
                    <button
                      className="ckout-coupon-btn"
                      onClick={applyCoupon}
                      disabled={!coupon.trim() || couponLoading}
                    >
                      {couponLoading ? "Checking…" : "Apply"}
                    </button>
                  )}
                </div>
                {message && (
                  <p className={`ckout-coupon-msg${msgOk ? " ckout-msg-ok" : " ckout-msg-err"}`}>
                    {message}
                  </p>
                )}
              </div>

              {paymentError && (
                <div
                  className="ckout-payment-error"
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 8,
                    background: "#fef2f2", border: "1px solid #fecaca",
                    color: "#b91c1c", borderRadius: 10,
                    padding: "10px 12px", margin: "0 0 14px",
                    fontSize: "0.85rem", fontWeight: 600, lineHeight: 1.5,
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {paymentError}
                </div>
              )}

              {/* Pay button */}
              <button
                className="ckout-pay-btn"
                onClick={handlePayment}
                disabled={loading}
              >
                {loading ? (
                  <><span className="ckout-spinner" /> Processing payment…</>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                    Pay ₹{finalAmount.toLocaleString()} Securely
                  </>
                )}
              </button>

              <p className="ckout-pay-note">
                Powered by <strong>Razorpay</strong> · UPI, Cards, Net Banking accepted
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Checkout;